// src/features/admin/components/settings/document-management/components/font-manager/FontSettings.tsx
import React, { useState, useEffect } from 'react';
import {
  Cog6ToothIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

// Mock fonts list - TODO: Fetch from API
const AVAILABLE_FONTS = [
  { font_uid: 'NimbusRoman-Regular.otf', displayName: 'Nimbus Roman', family: 'Nimbus Roman' },
  { font_uid: 'Inter-Regular.ttf', displayName: 'Inter', family: 'Inter' },
  { font_uid: 'Roboto-Regular.ttf', displayName: 'Roboto', family: 'Roboto' },
  { font_uid: 'TimesNewRoman.ttf', displayName: 'Times New Roman', family: 'Times New Roman' },
];

interface FontDefault {
  id: string;
  context: 'global' | 'template' | 'document-type';
  contextValue?: string; // Template name or document type
  fontFamily: string;
  fontSize: number;
}

/**
 * Font Settings Component
 * 
 * Configure default fonts for:
 * - Global application default
 * - Specific document templates
 * - Specific document types
 */
const FontSettings: React.FC = () => {
  const [defaults, setDefaults] = useState<FontDefault[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newDefault, setNewDefault] = useState<Partial<FontDefault>>({
    context: 'template',
    fontFamily: '',
    fontSize: 14
  });

  // Available templates and document types (mock data - would come from backend)
  const availableTemplates = [
    'Motion to Dismiss',
    'Response to Motion',
    'Complaint',
    'Answer',
    'Discovery Request',
    'Deposition Notice'
  ];

  const availableDocumentTypes = [
    'Pleading',
    'Motion',
    'Brief',
    'Letter',
    'Agreement',
    'Memorandum'
  ];

  useEffect(() => {
    loadDefaults();
  }, []);

  const loadDefaults = async () => {
    try {
      setLoading(true);
      
      // TODO: Fetch from backend API
      // Mock data
      const mockDefaults: FontDefault[] = [
        {
          id: '1',
          context: 'global',
          fontFamily: 'EdgeDocs Roman',
          fontSize: 14
        },
        {
          id: '2',
          context: 'template',
          contextValue: 'Motion to Dismiss',
          fontFamily: 'EdgeDocs Roman',
          fontSize: 12
        },
        {
          id: '3',
          context: 'document-type',
          contextValue: 'Brief',
          fontFamily: 'EdgeDocs Roman',
          fontSize: 13
        }
      ];

      setDefaults(mockDefaults);
    } catch (error) {
      console.error('Failed to load font defaults:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDefault = () => {
    if (!newDefault.fontFamily) return;

    const id = Date.now().toString();
    const defaultToAdd: FontDefault = {
      id,
      context: newDefault.context as 'global' | 'template' | 'document-type',
      contextValue: newDefault.contextValue,
      fontFamily: newDefault.fontFamily,
      fontSize: newDefault.fontSize || 14
    };

    setDefaults(prev => [...prev, defaultToAdd]);
    
    // Reset form
    setNewDefault({
      context: 'template',
      fontFamily: '',
      fontSize: 14
    });
  };

  const handleRemoveDefault = (id: string) => {
    setDefaults(prev => prev.filter(d => d.id !== id));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // TODO: Save to backend API
      // await axios.post('/api/fonts/defaults', { defaults });
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Font defaults saved:', defaults);
    } catch (error) {
      console.error('Failed to save font defaults:', error);
    } finally {
      setSaving(false);
    }
  };

  const getContextLabel = (item: FontDefault): string => {
    if (item.context === 'global') return 'Global Default';
    if (item.context === 'template') return `Template: ${item.contextValue}`;
    if (item.context === 'document-type') return `Document Type: ${item.contextValue}`;
    return 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lex-accent mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading font settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="font-settings">
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Default Font Configuration
        </h4>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Set default fonts that will be automatically applied when users create new documents. More specific defaults (templates) override general ones (global).
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Current Defaults */}
        <div className="lg:col-span-2 space-y-6">
          {/* Global Default (Always shown) */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h5 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
                <Cog6ToothIcon className="h-5 w-5 mr-2 text-lex-accent" />
                Global Default Font
              </h5>
              <span className="px-2 py-1 bg-lex-accent-soft text-lex-accent text-xs font-medium rounded">
                Fallback
              </span>
            </div>
            
            {defaults.filter(d => d.context === 'global').map(item => (
              <div key={item.id} className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Font Family
                  </label>
                  <select
                    value={item.fontFamily}
                    onChange={(e) => {
                      setDefaults(prev => prev.map(d =>
                        d.id === item.id ? { ...d, fontFamily: e.target.value } : d
                      ));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                  >
                    {AVAILABLE_FONTS.map(font => (
                      <option key={font.font_uid} value={font.family}>
                        {font.displayName}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Font Size (pt)
                  </label>
                  <input
                    type="number"
                    min="8"
                    max="72"
                    value={item.fontSize}
                    onChange={(e) => {
                      setDefaults(prev => prev.map(d =>
                        d.id === item.id ? { ...d, fontSize: parseInt(e.target.value) } : d
                      ));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                  />
                </div>
              </div>
            ))}
            
            {defaults.filter(d => d.context === 'global').length === 0 && (
              <div className="text-sm text-gray-500 dark:text-gray-400 italic">
                No global default set. Using system default.
              </div>
            )}
          </div>

          {/* Template & Document Type Defaults */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center justify-between mb-4">
              <h5 className="text-base font-semibold text-gray-900 dark:text-white flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2 text-lex-accent" />
                Template & Document Type Overrides
              </h5>
            </div>

            {/* List of overrides */}
            <div className="space-y-3 mb-4">
              {defaults.filter(d => d.context !== 'global').map(item => (
                <div 
                  key={item.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                >
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {getContextLabel(item)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {item.fontFamily} • {item.fontSize}pt
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveDefault(item.id)}
                    className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                    title="Remove override"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
              
              {defaults.filter(d => d.context !== 'global').length === 0 && (
                <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                  No overrides configured. Add one using the form on the right.
                </div>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex items-center justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-lex-accent text-lex-accent-contrast rounded-md hover:bg-lex-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-5 w-5 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>

        {/* Add New Override Form */}
        <div className="lg:col-span-1">
          <div className="sticky top-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <PlusIcon className="h-5 w-5 mr-2 text-green-500" />
              Add Override
            </h5>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Apply To
                </label>
                <select
                  value={newDefault.context}
                  onChange={(e) => setNewDefault(prev => ({ ...prev, context: e.target.value as any, contextValue: undefined }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                >
                  <option value="template">Template</option>
                  <option value="document-type">Document Type</option>
                </select>
              </div>

              {newDefault.context === 'template' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Template
                  </label>
                  <select
                    value={newDefault.contextValue || ''}
                    onChange={(e) => setNewDefault(prev => ({ ...prev, contextValue: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                  >
                    <option value="">Select template...</option>
                    {availableTemplates.map(template => (
                      <option key={template} value={template}>{template}</option>
                    ))}
                  </select>
                </div>
              )}

              {newDefault.context === 'document-type' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Document Type
                  </label>
                  <select
                    value={newDefault.contextValue || ''}
                    onChange={(e) => setNewDefault(prev => ({ ...prev, contextValue: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                  >
                    <option value="">Select type...</option>
                    {availableDocumentTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Font
                </label>
                <select
                  value={newDefault.fontFamily || ''}
                  onChange={(e) => setNewDefault(prev => ({ ...prev, fontFamily: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                >
                  <option value="">Select font...</option>
                  {AVAILABLE_FONTS.map(font => (
                    <option key={font.font_uid} value={font.family}>
                      {font.displayName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Size (pt)
                </label>
                <input
                  type="number"
                  min="8"
                  max="72"
                  value={newDefault.fontSize || 14}
                  onChange={(e) => setNewDefault(prev => ({ ...prev, fontSize: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-lex-accent focus:border-transparent"
                />
              </div>

              <button
                onClick={handleAddDefault}
                disabled={!newDefault.fontFamily || !newDefault.contextValue}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center text-sm"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Override
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="mt-6 bg-lex-accent-soft border border-lex-accent/30 rounded-lg p-4">
        <div className="flex">
          <Cog6ToothIcon className="h-5 w-5 text-lex-accent mr-3 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-lex-accent">
            <p className="font-medium mb-1">Font Override Priority</p>
            <p>
              Template-specific fonts override document type defaults, which override the global default. 
              Users can still manually change fonts when editing documents.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FontSettings;

